import React, { useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import "./DashboardContent.css";
import Dashboardmilestonesection from "./dashboardmilestonesection";
import Dashboardbadgessection from "./dashboardbadgessection";
import Dashboardpointssection from "./dashboardpointssection";
import Dashboardonestepsection from "./dashboardonestepsection";
import { getLearnerCourses } from "Redux/CourseDetails";
import { getCategoryPoints, fetchFoundationStatus } from "Redux/LearnersReport";
import { getUserData } from "Redux/UserDetails";
import { getCoursesGroupedByCategoryAndModule, isCourseEligible } from "utils/foundationUnlock";
import AOS from "aos";
import "aos/dist/aos.css";

const computeEarnedBadges = (completed = []) => {
	const badges = {
		bronze: { earned: false, count: 0 },
		silver: { earned: false, count: 0 },
		gold: { earned: false, count: 0 },
		diamond: { earned: false, count: 0 },
		platinum: { earned: false, count: 0 },
	};

	completed.forEach(course => {
		if (!course.badge) return;

		const key = course.badge.trim().toLowerCase();
		if (badges[key]) {
			badges[key].earned = true;
			badges[key].count += 1;
		}
	});

	return badges;
};

const computeHubProgress = ({ hub, userData, userLevel }) => {
	if (!hub?.courses?.length) return 0;

	const eligibleMandatory = hub.courses.filter(
		c =>
			c.courseType === "Mandatory" &&
			isCourseEligible(c, userData, userLevel)
	);

	const eligibleOptional = hub.courses.filter(
		c =>
			c.courseType === "Optional" &&
			isCourseEligible(c, userData, userLevel)
	);

	// Case 1: Mandatory eligible courses exist
	if (eligibleMandatory.length > 0) {
		const completedMandatory = eligibleMandatory.filter(
			c => c.completionDate
		);
		return completedMandatory.length / eligibleMandatory.length;
	}

	// Case 2: No mandatory → fallback to optional
	if (eligibleOptional.length > 0) {
		const completedOptional = eligibleOptional.filter(
			c => c.completionDate
		);
		return completedOptional.length / eligibleOptional.length;
	}

	// Nothing eligible → no progress
	return 0;
};

function DashboardContent() {
	const { instance } = useMsal();
	const dispatch = useDispatch();
	const activeAccount = instance.getActiveAccount();
	const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;
	const userLevel = activeAccount?.idTokenClaims?.jobFamilyDescription;

	const { coursesByHub } = useSelector((state) => state.courseDetails);
	const userData = useSelector(state => state.userDetails.userData?.[0]);
	const { categoryPoints } = useSelector(state => state.learnersReport);

	// Memoized computation of dashboard counts using utils
	const { completedCourses, badgesEarned, totalPoints, averageHubProgress } = useMemo(() => {
		if (!coursesByHub || !userData) return { completedCourses: [], badgesEarned: [], totalPoints: 0, averageHubProgress: 0 };

		let completedCourses = [];
		let badgesEarned = [];
		let totalPoints = 0;
		let hubProgressSum = 0;

		coursesByHub.forEach(hub => {
			const hubProgress = computeHubProgress({
				hub,
				userData,
				userLevel,
			});

			hubProgressSum += hubProgress;

			const groupedCourses = getCoursesGroupedByCategoryAndModule({
				coursesByHub: [hub],
				userData,
				userLevel,
				hubName: hub.hubName
			});

			Object.values(groupedCourses).forEach(category => {
				Object.values(category).forEach(moduleObj => {
					// All mandatory courses in module
					const mandatoryCourses = moduleObj.Mandatory || [];
					const optionalCourses = moduleObj.Optional || [];

					// Add to completed courses list if completed
					const completedInModule = [...mandatoryCourses, ...optionalCourses].filter(c => c.completionDate);
					completedCourses.push(...completedInModule);

					// Add badges
					const completedMandatory = mandatoryCourses.filter(c => c.completionDate);
					badgesEarned.push(...completedMandatory);

					// Update total points
					totalPoints += completedInModule.reduce((acc, c) => acc + (c.coursePoints || 0), 0);
				});
			});
		});

		const averageHubProgress =
			coursesByHub.length > 0
				? Math.round((hubProgressSum / coursesByHub.length) * 100)
				: 0;

		return { completedCourses, badgesEarned, totalPoints, averageHubProgress };
	}, [coursesByHub, userData, userLevel]);

	const completedCount = completedCourses.length;
	// const hubProgressPercentage = Math.round((completedHubsCount / (coursesByHub?.length || 7)) * 100);
	const computedBadges = computeEarnedBadges(badgesEarned);

	useEffect(() => {
		if (!enterpriseId) return;

		dispatch(getLearnerCourses(enterpriseId));
		dispatch(getCategoryPoints(enterpriseId, "ALL"));
		dispatch(fetchFoundationStatus(enterpriseId));
		dispatch(getUserData(enterpriseId));
	}, [enterpriseId, dispatch]);

	useEffect(() => {
		const handleAOS = () => {
			if (window.innerWidth > 768) {
				AOS.init({ duration: 800 });
			} else {
				AOS.refreshHard();
				document.querySelectorAll("[data-aos]").forEach((el) => {
					el.removeAttribute("data-aos");
				});
			}
		};
		handleAOS();
		window.addEventListener("resize", handleAOS);
		return () => window.removeEventListener("resize", handleAOS);
	}, []);

	return (
		<>
			{/* Banner and Progress sections remain unchanged */}
			<div className="few-pages-banner-wrap">
				<img
					src={process.env.PUBLIC_URL + "/Assets/AllBanners/dashboard-banner-latest.png"}
					alt="Ignite banner"
					className="banner-img"
				/>
				<div className="few-pages-banner-box">
					<div className="few-pages-banner-content">
						<h1 className="banner-main-head">Your Learning. Your Progress</h1>
						<p className="banner-sub-text">
							A personalized view of your learning journey, track progress,
							achievements, and milestones.
						</p>
					</div>
				</div>
			</div>

			<div className="progress-glance-section">
				<h2 className="section-headings">Learning Progress at a Glance</h2>
				<p className="section-sub-text">
					A unified view of learning activity across Learning Hubs, milestones,
					points, and badges bringing structure and visibility to your
					individual learning journey.
				</p>
				<div className="progress-glance-wrap">
					<div className="progress-glance-row row1">
						<div className="progress-glance-box">
							<h3 className="h3-head">Your Progress</h3>
							<div className="progress-bar-box">
								<div className="progress-container">
									<div className="progress-header">
										<span className="progress-label">
											Overall progress across Learning Hubs
										</span>
										<span className="progress-value">{averageHubProgress}%</span>
									</div>
									<div className="progress-track">
										<div
											className="progress-fill"
											style={{ width: `${averageHubProgress}%` }}
										/>
									</div>
								</div>
							</div>

							<div className="dashboard-course-point-badge-wrap">
								<div className="dashboard-course-point-badge-box" data-aos="fade-left">
									<div className="dashboard-courses-box">
										<img
											src={process.env.PUBLIC_URL + "/Assets/Dashboard/courses-icon.png"}
											alt="icon"
											className="dashboard-all-icon"
										/>
									</div>
									<h3>
										Courses completed <br /> <span>{completedCount}</span>
									</h3>
								</div>

								<div className="dashboard-course-point-badge-box" data-aos="zoom-in">
									<div className="dashboard-points-box">
										<img
											src={process.env.PUBLIC_URL + "/Assets/Dashboard/points-icon.png"}
											alt="icon"
											className="dashboard-all-icon"
										/>
									</div>
									<h3>
										Points Earned <br /> <span>{totalPoints}</span>
									</h3>
								</div>

								<div className="dashboard-course-point-badge-box" data-aos="fade-right">
									<div className="dashboard-badges-box">
										<img
											src={process.env.PUBLIC_URL + "/Assets/Dashboard/badges-icon.png"}
											alt="icon"
											className="dashboard-all-icon"
										/>
									</div>
									<h3>
										Badges Earned <br /> <span>{Object.values(computedBadges).reduce((acc, b) => acc + b.count, 0)}</span>
									</h3>
								</div>
							</div>

							<div className="dashboard-onestep-wrap">
								<div className="dashboard-onestep-row row1" data-aos="fade-left">
									<div className="dashboard-onestep-box">
										<img
											src={process.env.PUBLIC_URL + "/Assets/Dashboard/one-step-img.png"}
											alt="icon"
											className="one-step-img"
										/>
									</div>
								</div>
								<div className="dashboard-onestep-row row2" data-aos="fade-right">
									<div className="dashboard-onestep-box">
										<Dashboardonestepsection />
									</div>
								</div>
							</div>

							<div className="dashboard-milestone-sec">
								<h3 className="h3-head">Milestone Progress</h3>
								<p className="h3-below-text">Track your achievements</p>
								<Dashboardmilestonesection />
							</div>
						</div>
					</div>

					<div className="progress-glance-row row2">
						<div className="progress-glance-box">
							<div className="dashboard-badges-sec">
								<Dashboardbadgessection earnedBadges={computedBadges} />
							</div>
							<div className="dashboard-points-sec">
								<Dashboardpointssection />
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export default DashboardContent;
